<script setup lang="ts">
/**
 * PageHero Component
 * 
 * A generic hero section for pages, featuring an animated background or image,
 * a badge, a main title, a description, and optional action buttons.
 */

interface Props {
  // Title Parts
  titlePrefix?: string
  titleHighlight?: string
  titleSuffix?: string
  
  // Single block title (optional)
  title?: string
  
  badgeText?: string
  description?: string
  
  // Visuals
  image?: string
  height?: string
  // Highlight mode
  highlightMode?: 'solid' | 'gradient'
  highlightColor?: 'red' | 'yellow'
  
  // Layout & Visuals
  align?: 'center' | 'left'
  showAbstract?: boolean
  showSeparator?: boolean
  tags?: string[]
  
  // Overlay
  overlayOpacity?: string // Tailwind class for opacity, e.g., 'bg-oxo-dark/80'

  // Action Buttons
  primaryButton?: { text: string; to: string; umamiEvent?: string }
  secondaryButton?: { text: string; to: string; umamiEvent?: string }

  // Centralized Hero
  pageKey?: string

  // Background video (hero mode)
  videoSrc?: string
}

const props = withDefaults(defineProps<Props>(), {
  titlePrefix: '',
  titleHighlight: '',
  titleSuffix: '',
  title: '',
  badgeText: '',
  description: '',
  highlightMode: 'gradient',
  highlightColor: 'red',
  height: '60vh',
  align: 'center',
  showAbstract: undefined,
  showSeparator: false,
  tags: () => [],
  overlayOpacity: 'bg-hero-overlay', // Standardized overlay
  primaryButton: undefined,
  secondaryButton: undefined,
  pageKey: '',
  videoSrc: ''
})

const PAGE_KEY_TO_VIDEO_LOCATION: Record<string, string> = {
  home: 'home-hero',
  school: 'school-hero',
  formations: 'formations-hero',
  projects: 'projets-hero',
  news: 'actus-hero',
  faq: 'faq-hero',
  contact: 'contact-hero',
  candidature: 'candidature-hero',
  workshops: 'ateliers-hero'
}

const { getHero } = usePageHero()
const { data: centralHero } = props.pageKey
  ? await useAsyncData(`hero-${props.pageKey}`, () => getHero(props.pageKey!))
  : { data: ref(null) }

// Auto-fetch video if heroMode is 'video'
const { getAll: getAllVideos } = useSiteVideos()
const videoLocation = computed(() => props.pageKey ? PAGE_KEY_TO_VIDEO_LOCATION[props.pageKey] || '' : '')
const isVideoMode = computed(() => centralHero.value?.heroMode === 'video')

const { data: heroVideos } = isVideoMode.value && videoLocation.value
  ? await useAsyncData(`hero-video-${props.pageKey}`, () => getAllVideos())
  : { data: ref(null) }

const autoVideoSrc = computed(() => {
  if (!isVideoMode.value) return ''
  if (!heroVideos.value) return ''
  return heroVideos.value.find((v: any) => v.location === videoLocation.value)?.videoUrl || ''
})

const effectiveVideoSrc = computed(() => {
  if (props.videoSrc) return props.videoSrc
  return autoVideoSrc.value
})

const computedImage = computed(() => {
  if (centralHero.value?.imageHorizontal) return centralHero.value.imageHorizontal
  return props.image
})

const computedAlt = computed(() => {
  if (centralHero.value) return centralHero.value.imageAlt
  return props.title || props.titleHighlight || ''
})

const shouldShowAbstract = computed(() => {
  if (props.showAbstract !== undefined) return props.showAbstract
  return !computedImage.value && !effectiveVideoSrc.value
})

const highlightClasses = computed(() => {
  if (props.highlightMode === 'solid') {
    return props.highlightColor === 'yellow' ? 'text-oxo-yellow' : 'text-oxo-red'
  }
  // Default gradient mode matching SchoolHero
  return 'text-transparent bg-clip-text bg-linear-to-r from-oxo-red to-oxo-yellow'
})

const heroRef = ref<HTMLElement | null>(null)
const heroVideo = ref<HTMLVideoElement | null>(null)
const isVideoPlaying = ref(false)
const { isPaused: isVideoPaused, toggle: toggleVideo } = useBackgroundVideo(heroVideo, effectiveVideoSrc)

const HERO_SIZES = 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw'
const HERO_MODIFIERS = { quality: 80, format: 'webp' }
// Rendered width of an object-cover image filling the hero (min-height: 100vh - header):
// the larger of the viewport width and the hero height times the image ratio. Declaring it
// keeps the image's intrinsic size at least as large as the hero, so the browser picks a
// sharp enough file and measures the image, not the video fading in later, as the LCP.
const VERTICAL_RATIO = 9 / 16
const HORIZONTAL_RATIO = 16 / 9
// `sizes` cannot read CSS variables: header heights mirror --header-height in main.css.
const coverWidth = (ratio: number) => {
  const width = (header: string) => `max(100vw, calc((100vh - ${header}) * ${ratio.toFixed(4)}))`
  return `(max-width: 767px) ${width('3rem')}, ${width('6rem')}`
}
const VERTICAL_IMAGE_SIZES = coverWidth(VERTICAL_RATIO)
const HORIZONTAL_IMAGE_SIZES = coverWidth(HORIZONTAL_RATIO)
const img = useImage()
const horizontalImage = computed(() => computedImage.value ? img.getSizes(computedImage.value, { sizes: HERO_SIZES, modifiers: HERO_MODIFIERS }) : null)
const verticalImage = computed(() => centralHero.value?.imageVertical ? img.getSizes(centralHero.value.imageVertical, { sizes: HERO_SIZES, modifiers: HERO_MODIFIERS }) : null)

const scrollToContent = () => {
  if (heroRef.value) {
    const nextSection = heroRef.value.nextElementSibling as HTMLElement
    if (nextSection) {
      const header = document.querySelector('header')
      const offset = header ? header.offsetHeight : 80
      const target = nextSection.offsetTop - offset
      window.scrollTo({
        top: target,
        behavior: 'smooth'
      })
    }
  }
}
</script>

<template>
  <section 
    ref="heroRef"
    class="relative w-full flex flex-col items-center"
    :style="height === 'screen' ? { minHeight: 'calc(100vh - var(--header-height, 80px))' } : { minHeight: height }"
    :class="[
      align === 'center' ? 'text-center' : 'text-left'
    ]"
  >
    <div v-if="computedImage || effectiveVideoSrc" class="absolute inset-0 z-0">
      <!-- Hero image: one <picture>, so each viewport downloads a single image -->
      <picture v-if="computedImage">
        <source
          v-if="verticalImage"
          media="(max-width: 767px)"
          :srcset="verticalImage.srcset"
          :sizes="VERTICAL_IMAGE_SIZES"
        />
        <img
          :src="horizontalImage!.src"
          :srcset="horizontalImage!.srcset"
          :sizes="HORIZONTAL_IMAGE_SIZES"
          :alt="computedAlt"
          class="w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
        />
      </picture>

      <!-- Background video: loaded after the page, fades in over the image once playing -->
      <video
        v-if="effectiveVideoSrc"
        ref="heroVideo"
        muted
        loop
        playsinline
        preload="none"
        class="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
        :class="isVideoPlaying ? 'opacity-100' : 'opacity-0'"
        @playing="isVideoPlaying = true"
      ></video>

      <!-- Overlays scaled for better readability - Configurable -->
      <div
        class="absolute inset-0 z-10"
        :class="overlayOpacity"
      ></div>
    </div>

    <!-- Animated Background elements (Abstract) - Unified with modern OXO glow -->
    <div v-if="shouldShowAbstract" class="absolute inset-0 z-0 bg-background transition-colors duration-300">
      <div class="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--tw-gradient-stops))] from-oxo-red/5 via-transparent to-transparent opacity-50"></div>
      <div class="absolute h-[500px] w-[500px] -top-64 -left-64 bg-oxo-red/5 rounded-full blur-3xl animate-pulse"></div>
      <div class="absolute h-[600px] w-[600px] -bottom-96 -right-96 bg-oxo-yellow/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
    </div>

    <!-- Content Wrapper: Flex-1 to push footer down - Standardized with HomeHero -->
    <div class="relative z-30 container mx-auto px-container-px flex-1 flex flex-col justify-center w-full py-10 md:py-24">
      <div 
        :class="[
          'max-w-[100rem] transition-all duration-1000 delay-300 w-full flex flex-col',
          align === 'center' ? 'items-center text-center mx-auto' : 'items-center text-center md:items-start md:text-left'
        ]"
      >

        <!-- Optional Badge -->
        <div v-if="badgeText || $slots.badge" class="mb-8 animate-fade-in-up">
           <slot name="badge">
             <div class="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 rounded-full border border-white/10 backdrop-blur-md">
                <span class="w-1.5 h-1.5 rounded-full bg-oxo-red animate-pulse"></span>
                <span class="text-[10px] font-black uppercase tracking-[0.3em] text-foreground">{{ badgeText }}</span>
             </div>
           </slot>
        </div>

        <!-- Main Title Section -->
        <div class="mb-4 md:mb-8">
          <slot name="title">
           <h1
             :aria-label="[titlePrefix, titleHighlight, titleSuffix].filter(Boolean).join(' ') || title || undefined"
             :class="[
             'font-display font-black uppercase leading-[1.1] flex flex-wrap gap-y-2 py-4 items-baseline',
             'text-4xl md:text-5xl lg:text-5xl xl:text-6xl text-foreground drop-shadow-2xl',
             align === 'center' ? 'justify-center' : 'justify-center md:justify-start'
           ]">
             <span v-if="titlePrefix" class="mr-[0.2em]">{{ titlePrefix }}</span>
             <span
               v-if="titleHighlight"
               class="inline-block px-[0.05em] mx-[-0.05em]"
               :style="{
                 backgroundImage: highlightMode === 'gradient' ? 'linear-gradient(to right, #ff3b3b, #f7c432, #ff3b3b)' : 'none',
                 '-webkit-background-clip': highlightMode === 'gradient' ? 'text' : 'none',
                 'background-clip': highlightMode === 'gradient' ? 'text' : 'none',
                 color: highlightMode === 'gradient' ? 'transparent' : 'var(--oxo-red)',
                 backgroundSize: '200% auto'
               }"
             >
               {{ titleHighlight }}
             </span>

             <span v-if="titleSuffix" class="ml-[0.2em]">{{ titleSuffix }}</span>
             <span v-else-if="title && !titleHighlight">{{ title }}</span>
           </h1>
          </slot>

           <!-- Signature Separator Bar -->
           <div v-if="showSeparator"
             class="h-1.5 bg-linear-to-r from-oxo-red via-oxo-yellow to-oxo-red rounded-full animate-width-grow mt-4"
             :class="align === 'center' ? 'mx-auto' : 'mx-auto md:mx-0 w-32'"
           ></div>
        </div>


        <!-- Info/Metadatas (Date, Category, duration...) -->
        <div v-if="$slots.info" class="mb-6">
           <slot name="info" />
        </div>

        <!-- Description & Tags -->
        <div v-if="description || tags.length > 0" 
          class="mb-6 md:mb-12 max-w-2xl"
          :class="[
            align === 'center' ? 'text-center mx-auto' : 'text-center md:text-left md:border-l-4 md:border-oxo-red md:pl-6 md:mx-0 mx-auto'
          ]"
        >
          <p v-if="description" class="text-sm sm:text-lg md:text-xl text-foreground-dim font-light leading-relaxed">
            {{ description }}
          </p>
          <div v-if="tags.length > 0" class="mt-4 flex flex-wrap gap-x-2 gap-y-1" :class="align === 'center' ? 'justify-center text-[10px] md:text-base' : 'justify-center md:justify-start text-[10px] md:text-base md:tracking-widest font-medium text-foreground uppercase'">
            <template v-for="(tag, index) in tags" :key="tag">
                <span>{{ tag }}</span>
                <span v-if="index < tags.length - 1" class="w-1 h-1 rounded-full bg-oxo-red my-auto mx-2 md:mx-3"></span>
            </template>
          </div>
        </div>

        <!-- Action Buttons (prop-based) -->
        <div v-if="primaryButton || secondaryButton" class="flex flex-col sm:flex-row items-center gap-6 w-full sm:w-auto justify-center md:justify-start pb-8 md:pb-0 animate-fade-in-up" style="animation-delay: 0.6s">
          <NuxtLink 
            v-if="primaryButton"
            :to="primaryButton.to" 
            class="btn-primary group w-full sm:w-auto text-center"
            :data-umami-event="primaryButton.umamiEvent"
          >
            {{ primaryButton.text }}
          </NuxtLink>

          <NuxtLink 
            v-if="secondaryButton"
            :to="secondaryButton.to" 
            class="btn-secondary group w-full sm:w-auto text-center"
            :data-umami-event="secondaryButton.umamiEvent"
          >
            {{ secondaryButton.text }}
          </NuxtLink>
        </div>

        <!-- Actions Slot -->
        <div v-if="$slots.actions" class="mt-12 animate-fade-in-up delay-1000">
          <slot name="actions" />
        </div>

      </div>
    </div>
    
    <!-- Scroll indicator -->
    <div class="relative z-40 w-full flex justify-center pb-8 shrink-0">
      <button 
        @click="scrollToContent"
        type="button"
        class="animate-bounce group cursor-pointer flex flex-col items-center gap-2"
      >
         <span class="text-[10px] font-black uppercase tracking-[0.4em] mr-[-0.4em] text-foreground-dim group-hover:text-oxo-red transition-colors duration-300">Découvrir</span>
         <i class="fas fa-chevron-down text-lg text-foreground-dim group-hover:text-oxo-red transition-all duration-300 drop-shadow-lg" aria-hidden="true"></i>
      </button>
    </div>

    <!-- Background video pause control (WCAG 2.2.2) -->
    <button
      v-if="effectiveVideoSrc"
      type="button"
      class="absolute bottom-6 right-6 z-40 w-11 h-11 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-oxo-red transition-colors duration-300"
      :aria-label="isVideoPaused ? 'Lire la vidéo' : 'Mettre la vidéo en pause'"
      @click="toggleVideo"
    >
      <i :class="isVideoPaused ? 'fas fa-play' : 'fas fa-pause'" aria-hidden="true"></i>
    </button>

  </section>



</template>


<style scoped>
/* Any specific local adjustments can go here */
</style>
